import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { fetchDocDashboard, type DocDashboardPayload } from '../../api/docs';
import {
  DEFAULT_FILTERS,
  STATE_ORDER,
  buildDocRows,
  filterRows,
  kindLabelOf,
  orderedLanguages,
  sortRows,
  summarizeRows,
  type DocRow,
  type DocRowDoc,
  type DocRowState,
  type DocSortKey,
  type DocTableFilters,
} from '../../lib/docTable';
import { WORKFLOW_DISPLAY } from '../../lib/docs';
import { buildHiddenSets, canAiAdmin, canDocBtn, canEditMap, isPermHidden } from '../../lib/permissions';
import { capAi } from '../../lib/capabilities';
import { canAssign, canEditDoc, canEditPrompt } from '../../lib/responsibility';
import type { DocPerm, NodeId } from '../../lib/types';
import {
  AiCell,
  DocumentCell,
  LanguagesCell,
  LastChangeCell,
  NodeCell,
  ResponsibleCell,
  StateCell,
  stateColorOf,
  stateLabelOf,
  type PickLocale,
} from './documentsCells';
import { LocalePopover } from './LocalePopover';

/**
 * The dashboard's DOCUMENTS TAB (V0.44) — every document that exists or that a documentation
 * plan owes, one row each, with where it stands in every language.
 *
 * WHAT THIS FILE IS NOT. It derives nothing: `lib/docTable.ts` builds, filters, sorts and
 * counts the rows, `lib/health.ts` decides what a plan still owes, `lib/responsibility.ts`
 * answers who may assign, and `actions.ts` owns every write (including the regeneration
 * guard's 409 → confirm → force dance, which is deliberately NOT reimplemented here). This
 * file renders and wires. A predicate written here would be a second copy of one that already
 * has a test.
 *
 * FOUR DECISIONS WORTH STATING:
 *
 * THE FETCH IS KEYED ON `s.docWorkflow` AND `s.docRevision`, NOT ON A TIMER. Every action this
 * table offers — translate, regenerate, assign — replaces the workflow map as it lands (the
 * snapshot publishes it BY REFERENCE since V0.44.1, so identity means "it moved"); and the
 * revision counter moves whenever a document is closed, saved, restored or rolled back
 * (`renderDocLogs()`), which is exactly when a row's facts change from OUTSIDE this table — a
 * page created from an owed pill and closed unwritten comes back as owed the moment the rollback
 * lands. `createDocForNode` still bumps `refreshSeq` for the moment of creation. It is
 * deliberately NOT keyed on `s.currentLocale`: the payload is locale-complete, so switching
 * language re-reads the same rows in place — no refetch, no scroll jump, no remount.
 *
 * A DOCUMENT OPENS OVER THE DASHBOARD, NOT INSTEAD OF IT (V0.44.1). The overlay covers the whole
 * viewport (z 74 over z 70), `S.adminScreen` stays `'documents'`, and closing the document lands
 * the URL back here because the router prefers doc > admin. So opening a page — from its pill, the
 * eye, or the popover's "Open in X" — leaves the tab exactly as it was, filters and scroll
 * included, and the document's Close reads "Back to Documents". Only "locate" closes the
 * dashboard: the map has to be visible for that.
 *
 * THE AI GATE IS THE PAYLOAD, NOT THE ROLE. The server omits `aiByNode` for anyone who is not
 * an AI administrator, so `payload.aiByNode !== undefined` is the only thing that draws the AI
 * column and its three filters. Keying on the role instead would draw a column with nothing in
 * it the moment the two disagree — which is exactly what a server-side scoping change would do.
 *
 * NO WORKFLOW VERBS. Approve, request review and send back live in the document's own bar,
 * where the text being judged is on screen. This table says where things stand and gets you
 * there; it never clears a backlog on your behalf.
 */

const KIND_OPTIONS: readonly DocPerm[] = ['documentation', 'internal', 'course', 'demo', 'custom'];

/** How long a finished row action leaves its result beside the button. */
const ROWSTATE_MS = 2500;

interface RowBusy {
  cls: 'generating' | 'done' | 'error';
  text: string;
}

interface PopTarget {
  anchor: HTMLElement;
  docId: string;
  locale: string;
  focusResponsibles: boolean;
}

export function DocumentsTab({
  onLeave,
  focusNodeId,
  onConfigure,
}: {
  /** Close the dashboard — only "locate" needs it now (see the header). */
  onLeave: () => void;
  /** A node handed over by the AI tab's "See WF state": start narrowed to its rows. */
  focusNodeId: NodeId | null;
  /** "Configure": hand this node to the AI generation tab. */
  onConfigure: (nodeId: NodeId) => void;
}) {
  const s = useAppState();

  const [payload, setPayload] = useState<DocDashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshSeq, setRefreshSeq] = useState(0);

  // The hand-off is read ONCE, as initial state: the dashboard mounts this tab fresh per select.
  const [filters, setFilters] = useState<DocTableFilters>(() => ({ ...DEFAULT_FILTERS, owner: focusNodeId }));
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState<DocSortKey>('node');
  const [asc, setAsc] = useState(true);

  const [busy, setBusy] = useState<Record<string, RowBusy>>({});
  const [pop, setPop] = useState<PopTarget | null>(null);

  const timers = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);
  useEffect(
    () => () => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    },
    [],
  );

  /* ------------------------------------------------------------------ the read ---- */

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fetchDocDashboard().then(
      (p) => {
        if (!live) return;
        setPayload(p);
        setLoading(false);
      },
      (e: Error) => {
        if (!live) return;
        setError(e.message);
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
  }, [s.docWorkflow, s.docRevision, refreshSeq]);

  // Typing is debounced into the filter; the input stays controlled by what was typed.
  useEffect(() => {
    const t = window.setTimeout(() => setFilters((f) => ({ ...f, text: searchText })), 250);
    return () => window.clearTimeout(t);
  }, [searchText]);

  /* ------------------------------------------------------------------- the rows ---- */

  const hidden = useMemo(() => buildHiddenSets(s.perm, s.tagEdges), [s.perm, s.tagEdges]);

  const rows = useMemo<DocRow[]>(() => {
    if (!payload) return [];
    return buildDocRows({
      nodes: s.nodes,
      edges: s.edges,
      docTitles: s.docTitles,
      docTypes: s.docTypes,
      docAiGenerated: s.docAiGenerated,
      payload,
      canDocBtn: (k) => canDocBtn(s.perm, k),
      isPermHidden: (id) => isPermHidden(id, s.edges, hidden),
      userDirectory: s.userDirectory,
      groupDirectory: s.groupDirectory,
      currentLocale: s.currentLocale,
    });
  }, [
    payload,
    s.nodes,
    s.edges,
    s.docTitles,
    s.docTypes,
    s.docAiGenerated,
    s.perm,
    hidden,
    s.userDirectory,
    s.groupDirectory,
    s.currentLocale,
  ]);

  const shown = useMemo(
    () =>
      sortRows(
        filterRows(rows, filters, { edges: s.edges, currentUserId: s.currentUser?.id ?? null }),
        sortKey,
        asc,
      ),
    [rows, filters, s.edges, s.currentUser, sortKey, asc],
  );
  const summary = useMemo(() => summarizeRows(rows), [rows]);

  /* ---------------------------------------------------------------- the gates ---- */

  const aiVisible = !!payload && payload.aiByNode !== undefined;
  // Source first, then by code (EN, DE, FR here) — the order every language strip shares.
  const languages = useMemo(
    () => (payload ? orderedLanguages(payload.languages, payload.sourceLocale) : []),
    [payload],
  );
  const sourceLocale = payload?.sourceLocale ?? s.currentLocale;
  const lang = s.currentLocale.toUpperCase();

  const canTranslateTo = useCallback(
    (row: DocRowDoc, code: string): boolean =>
      capAi(s.caps, 'translate') &&
      (canAiAdmin(s.perm) ||
        canEditDoc(s, row.docPageId, code) ||
        (!!s.currentUser && (row.byLocale[code]?.responsibles ?? []).includes(s.currentUser.id))),
    [s],
  );

  const translateTargets = useCallback(
    (row: DocRowDoc): string[] =>
      row.byLocale[sourceLocale]?.written
        ? languages.filter((l) => l.code !== sourceLocale && canTranslateTo(row, l.code)).map((l) => l.code)
        : [],
    [languages, sourceLocale, canTranslateTo],
  );

  const nameOf = useCallback(
    (id: string) => s.userDirectory.find((u) => u.id === id)?.displayName ?? id,
    [s.userDirectory],
  );
  const groupNameOf = useCallback(
    (id: string) => s.groupDirectory.find((g) => g.id === id)?.name ?? id,
    [s.groupDirectory],
  );

  /* --------------------------------------------------------------- the actions ---- */

  // The heatmap is derived from the same facts these actions change; refresh it where it is on.
  const afterAction = useCallback(() => {
    if (s.heatmapOn) void callAction('loadMapHealth');
  }, [s.heatmapOn]);

  const mark = (key: string, state: RowBusy | null) =>
    setBusy((b) => {
      if (state) return { ...b, [key]: state };
      const rest = { ...b };
      delete rest[key];
      return rest;
    });

  // Over the dashboard, not instead of it (see the header): no `onLeave()` here.
  const openDoc = (row: DocRowDoc) => {
    callAction('openDocInCurrentMode', row.docPageId, row.title, row.owners[0]?.nodeId ?? null);
  };

  const locate = (nodeId: NodeId) => {
    onLeave(); // the map has to be visible for this one
    callAction('revealNodeOnMap', nodeId);
  };

  const create = async (nodeId: NodeId, kind: DocPerm) => {
    // The editor this opens sits above the dashboard, so the tab stays where it is; closing the
    // editor unwritten rolls the page back (V0.43.3), and the revision counter that rollback
    // bumps is what re-reads this table.
    await (callAction('createDocForNode', nodeId, kind) as Promise<void>);
    setRefreshSeq((n) => n + 1);
    afterAction();
  };

  const regenerate = async (row: DocRowDoc) => {
    const nodeId = row.ai?.primaryNodeId;
    if (!nodeId) return;
    mark(row.key, { cls: 'generating', text: 'Generating…' });
    let ok = false;
    try {
      ok = await (callAction('generateDocForNode', nodeId) as Promise<boolean>);
    } catch {
      ok = false;
    }
    mark(row.key, ok ? { cls: 'done', text: 'Generated' } : { cls: 'error', text: 'Not generated' });
    afterAction();
    later(() => mark(row.key, null), ROWSTATE_MS);
  };

  const runTranslations = async (row: DocRowDoc, targets: string[]) => {
    mark(row.key, { cls: 'generating', text: 'Translating…' });
    let done = 0;
    let failed = 0;
    for (const code of targets) {
      try {
        if (await (callAction('translateDoc', row.docPageId, code) as Promise<boolean>)) done += 1;
        else failed += 1;
      } catch {
        failed += 1;
      }
    }
    mark(
      row.key,
      failed
        ? { cls: 'error', text: `${done} done, ${failed} failed` }
        : { cls: 'done', text: `Translated into ${targets.join(', ').toUpperCase()}` },
    );
    afterAction();
    later(() => mark(row.key, null), ROWSTATE_MS);
  };

  const translateRow = (row: DocRowDoc) => {
    const targets = translateTargets(row);
    if (!targets.length) return;
    if (
      !confirm(
        `Translate “${row.title}” into ${targets.join(', ').toUpperCase()}?\n\n` +
          `${sourceLocale.toUpperCase()} stays the master copy — translations are made from it, ` +
          `and each lands on “Generated” for a reviewer to clear.`,
      )
    ) {
      return;
    }
    void runTranslations(row, targets);
  };

  const translateOne = (row: DocRowDoc, code: string) => {
    if (
      !confirm(
        `Translate “${row.title}” into ${code.toUpperCase()}?\n\n` +
          `${sourceLocale.toUpperCase()} stays the master copy — the translation is made from it, ` +
          `and lands on “Generated” for a reviewer to clear.`,
      )
    ) {
      return;
    }
    setPop(null);
    void runTranslations(row, [code]);
  };

  const openInLocale = async (row: DocRowDoc, code: string) => {
    setPop(null);
    await (callAction('setLocale', code) as Promise<void>);
    callAction('openDocInCurrentMode', row.docPageId, row.title, row.owners[0]?.nodeId ?? null);
  };

  const focusOwner = filters.owner;
  const focusLabel = focusOwner ? s.nodes[focusOwner]?.label || 'this node' : '';

  const pick: PickLocale = (anchor, row, locale, focusResponsibles) =>
    setPop({ anchor, docId: row.docPageId, locale, focusResponsibles });

  /* ----------------------------------------------------------------- the strip ---- */

  const stripActive = (state: DocRowState) =>
    state === 'planned' ? filters.planned === 'only' : filters.state === state;

  const toggleStrip = (state: DocRowState) => {
    if (state === 'planned') {
      setFilters((f) => ({ ...f, planned: f.planned === 'only' ? 'include' : 'only' }));
    } else {
      setFilters((f) => ({ ...f, state: f.state === state ? 'all' : state }));
    }
  };

  const toggleSort = (k: DocSortKey) => {
    if (sortKey === k) setAsc((a) => !a);
    else {
      setSortKey(k);
      setAsc(true);
    }
  };
  const sortClass = (k: DocSortKey) =>
    `logs-sortable${sortKey === k ? ' on' : ''}${sortKey === k && asc ? ' asc' : ''}`;

  /* ---------------------------------------------------------- the controls, as data ---- */
  /* Four selects and four checkboxes of the same shape: written once, differing only in the
     vocabulary each offers and the field each writes. */

  const opts = (...pairs: [string, string][]) => pairs;

  const selects: { id: string; value: string; pick: (v: string) => void; options: [string, string][] }[] = [
    {
      id: 'dash-docs-kind',
      value: filters.kind,
      pick: (v) => setFilters((f) => ({ ...f, kind: v as DocTableFilters['kind'] })),
      options: [['all', 'All kinds'], ...KIND_OPTIONS.map((k): [string, string] => [k, kindLabelOf(k)])],
    },
    {
      id: 'dash-docs-state',
      value: filters.state,
      pick: (v) => setFilters((f) => ({ ...f, state: v as DocTableFilters['state'] })),
      options: [
        ['all', 'Any state'],
        ...WORKFLOW_DISPLAY.map((w): [string, string] => [w.key, w.label]),
        ['missing', `Not written in ${lang}`],
      ],
    },
    {
      id: 'dash-docs-resp',
      value: filters.responsible,
      pick: (v) => setFilters((f) => ({ ...f, responsible: v })),
      options: [
        ...opts(['all', 'Anyone responsible'], ['me', 'Mine'], ['unassigned', 'Unassigned']),
        ...s.userDirectory.map((u): [string, string] => [u.id, u.displayName]),
      ],
    },
    {
      id: 'dash-docs-planned',
      value: filters.planned,
      pick: (v) => setFilters((f) => ({ ...f, planned: v as DocTableFilters['planned'] })),
      options: opts(
        ['include', 'Planned: include'],
        ['only', 'Planned: only'],
        ['exclude', 'Planned: exclude'],
      ),
    },
  ];

  // The three AI filters follow the PAYLOAD, never the role: the server omits `aiByNode` for
  // anyone else, and no row would then carry the facts they filter on.
  const checks: { id: string; ai: boolean; label: string; on: boolean; set: (v: boolean) => void }[] = [
    { id: 'dash-docs-stale', ai: false, label: 'Stale translations', on: filters.stale, set: (v) => setFilters((f) => ({ ...f, stale: v })) },
    { id: 'dash-docs-aigen', ai: true, label: 'Generated by AI', on: filters.aiGenerated, set: (v) => setFilters((f) => ({ ...f, aiGenerated: v })) },
    { id: 'dash-docs-guard', ai: true, label: 'Human edits since generation', on: filters.guarded, set: (v) => setFilters((f) => ({ ...f, guarded: v })) },
    { id: 'dash-docs-noemb', ai: true, label: 'Not in the chatbot index', on: filters.noEmbeddings, set: (v) => setFilters((f) => ({ ...f, noEmbeddings: v })) },
  ];

  const heads: [DocSortKey, string][] = [
    ['title', 'Document'],
    ['node', 'Node'],
    ['state', `State · ${lang}`],
    ['stale', 'Languages'],
    ['responsible', `Responsible · ${lang}`],
    ['lastChange', 'Last change'],
  ];

  // Fixed layout reads these; the AI column is 290px of badges and buttons, the rest is text.
  const colWidths = aiVisible ? [250, 230, 140, 150, 170, 140, 290, 74] : [250, 230, 140, 150, 170, 140, 74];

  const cols = aiVisible ? 8 : 7;
  const popRow = pop
    ? (rows.find((r) => r.kind === 'doc' && r.docPageId === pop.docId) as DocRowDoc | undefined)
    : undefined;

  /* ---------------------------------------------------------------- the render ---- */

  return (
    <div id="dash-docs-tab">
      <div className="perm-section-lead">
        Every document that exists or is owed by a documentation plan — where it sits, where it
        stands in each language, who owns it, and what the AI did to it. Click a language chip or a
        responsible for that language’s details and actions.
      </div>

      <div id="dash-docs-strip">
        {STATE_ORDER.map((st) => (
          <button
            key={st}
            type="button"
            className={`dt-strip-chip${stripActive(st) ? ' on' : ''}`}
            data-state={st}
            onClick={() => toggleStrip(st)}
          >
            <span
              className={`dt-dot${st === 'missing' || st === 'planned' ? ' dash' : ''}`}
              style={{ '--c': stateColorOf(st) } as CSSProperties}
            />
            {stateLabelOf(st)}
            {st === 'missing' ? ` in ${lang}` : ''} <b>{summary.byState[st]}</b>
          </button>
        ))}
        <button
          type="button"
          className={`dt-strip-chip${filters.stale ? ' on' : ''}`}
          data-state="stale"
          onClick={() => setFilters((f) => ({ ...f, stale: !f.stale }))}
        >
          <span className="dt-dot dash" style={{ '--c': 'var(--review-in-review)' } as CSSProperties} />
          Stale translations <b>{summary.stale}</b>
        </button>
      </div>

      <div className="logs-controls">
        {focusOwner ? (
          <button
            type="button"
            className="dt-strip-chip on"
            id="dash-docs-focus-clear"
            title="Handed over from the AI generation tab — show every document again"
            onClick={() => setFilters((f) => ({ ...f, owner: null }))}
          >
            Showing {focusLabel}’s documents · Show all
          </button>
        ) : null}

        <input
          className="fld"
          id="dash-docs-search"
          placeholder="Search titles, nodes, people…"
          style={{ flex: 1, minWidth: 220 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        {selects.map((sel) => (
          <select
            key={sel.id}
            className="fld"
            id={sel.id}
            style={{ width: 'auto' }}
            value={sel.value}
            onChange={(e) => sel.pick(e.target.value)}
          >
            {sel.options.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        ))}

        {checks
          .filter((c) => aiVisible || !c.ai)
          .map((c) => (
            <label key={c.id} className={`filterrow${c.ai ? ' ai' : ''}`}>
              <input type="checkbox" id={c.id} checked={c.on} onChange={(e) => c.set(e.target.checked)} />
              <span>{c.label}</span>
            </label>
          ))}

        <span className="tgt-meta" id="dash-docs-count">
          {shown.length} shown · {rows.length} total
        </span>

        <button
          type="button"
          className="iconbtn"
          id="dash-docs-refresh"
          title="Refresh"
          aria-label="Refresh"
          onClick={() => setRefreshSeq((n) => n + 1)}
        >
          <svg className="ico">
            <use href="#i-undo" />
          </svg>
        </button>
      </div>

      <div className="card">
        <table className="utable" id="dash-docs">
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {heads.map(([k, label]) => (
                <th key={k} className={sortClass(k)} onClick={() => toggleSort(k)}>
                  {label}
                </th>
              ))}
              {aiVisible ? <th className="dt-th-ai">AI</th> : null}
              <th />
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan={cols} style={{ color: 'var(--ink-faint)' }}>
                  Could not load the documents ({error}).
                </td>
              </tr>
            ) : loading && !payload ? (
              <tr>
                <td colSpan={cols} style={{ color: 'var(--ink-faint)' }}>
                  Loading…
                </td>
              </tr>
            ) : !shown.length ? (
              <tr>
                <td colSpan={cols} style={{ color: 'var(--ink-faint)' }}>
                  No documents match these filters.
                </td>
              </tr>
            ) : (
              shown.map((row) => {
                const doc = row.kind === 'doc' ? row : null;
                const plannedNode = row.kind === 'planned' ? row.nodeId : null;
                const mayCreate =
                  plannedNode !== null && (canEditMap(s.perm) || canAssign(s, plannedNode));
                const primary = doc?.ai?.primaryNodeId ?? null;
                const showRegen =
                  !!primary && capAi(s.caps, 'docgen') && canEditPrompt(s, primary);
                const showTranslate = !!doc && translateTargets(doc).length > 0;

                return (
                  <tr
                    key={row.key}
                    data-key={row.key}
                    data-kind={row.kind}
                    {...(doc ? { 'data-doc': doc.docPageId } : { 'data-node': plannedNode ?? '' })}
                    className={
                      [
                        row.kind === 'planned' ? 'dt-planned' : '',
                        focusOwner && row.owners.some((o) => o.nodeId === focusOwner) ? 'flash' : '',
                      ]
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                  >
                    <td>
                      <DocumentCell
                        row={row}
                        docTypes={s.docTypes}
                        aiVisible={aiVisible}
                        onOpen={() => doc && openDoc(doc)}
                        onCreate={mayCreate && plannedNode ? () => void create(plannedNode, row.docKind) : undefined}
                      />
                    </td>
                    <td>
                      <NodeCell
                        owners={row.owners}
                        nodes={s.nodes}
                        isLight={s.isLight}
                        onLocate={locate}
                      />
                    </td>
                    <td>
                      <StateCell row={row} currentLocale={s.currentLocale} sourceLocale={sourceLocale} />
                    </td>
                    <td>
                      <LanguagesCell
                        row={row}
                        languages={languages}
                        sourceLocale={sourceLocale}
                        currentLocale={s.currentLocale}
                        onPick={pick}
                      />
                    </td>
                    <td>
                      <ResponsibleCell
                        row={row}
                        currentLocale={s.currentLocale}
                        nameOf={nameOf}
                        groupNameOf={groupNameOf}
                        onPick={pick}
                      />
                    </td>
                    <td>
                      <LastChangeCell row={row} aiVisible={aiVisible} />
                    </td>
                    {aiVisible ? (
                      <td className="dt-ai">
                        {doc ? (
                          <AiCell
                            row={doc}
                            languages={languages}
                            busy={busy[row.key]}
                            showRegen={showRegen}
                            showTranslate={showTranslate}
                            onRegen={() => void regenerate(doc)}
                            onTranslate={() => translateRow(doc)}
                            onConfigure={primary ? () => onConfigure(primary) : undefined}
                          />
                        ) : (
                          // An owed page has nothing to run on yet — but its node has a prompt
                          // to write. The AI tab, narrowed to that node, is where that happens.
                          <button
                            type="button"
                            className="aidoc-actbtn dt-configure"
                            title="Write this node's AI prompt and generate from it"
                            onClick={() => plannedNode && onConfigure(plannedNode)}
                          >
                            Configure
                          </button>
                        )}
                      </td>
                    ) : null}
                    <td>
                      <div className="dt-rowacts">
                        {doc ? (
                          <>
                            <button
                              type="button"
                              className="iconbtn dt-open"
                              title="Open the document"
                              aria-label="Open the document"
                              onClick={() => openDoc(doc)}
                            >
                              <svg className="ico">
                                <use href="#i-eye" />
                              </svg>
                            </button>
                            {doc.owners.length ? (
                              <button
                                type="button"
                                className="iconbtn dt-locate"
                                title="Show on the map"
                                aria-label="Show on the map"
                                onClick={() => locate(doc.owners[0].nodeId)}
                              >
                                <svg className="ico">
                                  <use href="#i-target" />
                                </svg>
                              </button>
                            ) : null}
                          </>
                        ) : mayCreate ? null : (
                          // The owed pill is the way to create the page (V0.44.1); this viewer
                          // may not, and the pill says so on hover.
                          <span className="tgt-meta" title="A branch responsible or a map editor can add it">
                            —
                          </span>
                        )}
                        {/* Translating from the popover is open to a doc responsible who never
                            sees the AI column, so the progress note has to have a home here
                            too — an action with no feedback reads as an action that failed. */}
                        {!aiVisible && busy[row.key] ? (
                          <span className={`rowstate ${busy[row.key].cls}`}>{busy[row.key].text}</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {popRow ? (
        <LocalePopover
          s={s}
          row={popRow}
          locale={pop!.locale}
          anchor={pop!.anchor}
          sourceLocale={sourceLocale}
          focusResponsibles={pop!.focusResponsibles}
          canTranslateTo={canTranslateTo}
          onClose={() => setPop(null)}
          onOpenDoc={(r, code) => void openInLocale(r, code)}
          onTranslate={translateOne}
        />
      ) : null}
    </div>
  );
}

